import { Form, TextField } from '../components/forms';
/**
 * RESET PASSWORD PAGE
 *
 * Reset password using token from email link.
 */

import { useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  BlockStack,
  Text,
  Banner,
  Box,
  Button,
} from '@shopify/polaris';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

// Password requirements (same as Register)
const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_REQUIREMENTS = [
  { test: (p: string) => p.length >= PASSWORD_MIN_LENGTH, message: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { test: (p: string) => /[A-Z]/.test(p), message: 'One uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p), message: 'One lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p), message: 'One number' },
];

export default function ResetPassword() {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getPasswordStrength = useCallback((pwd: string) => {
    const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(pwd));
    return {
      score: passed.length,
      total: PASSWORD_REQUIREMENTS.length,
      requirements: PASSWORD_REQUIREMENTS.map(req => ({
        ...req,
        met: req.test(pwd),
      })),
    };
  }, []);

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (passwordStrength.score < PASSWORD_REQUIREMENTS.length) {
      setError('Password does not meet requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.code === 'TOKEN_EXPIRED') {
          setError('This reset link has expired. Please request a new one.');
        } else if (result.code === 'TOKEN_INVALID') {
          setError('This reset link is invalid or has already been used.');
        } else {
          setError(result.error || 'Failed to reset password');
        }
        return;
      }

      // Success - redirect to login
      navigate('/login?success=password_reset', { replace: true });
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [token, password, passwordStrength, passwordsMatch, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Invalid Token */}
          {!token ? (
            <Card>
              <BlockStack gap="400">
                <Banner tone="critical">
                  {error || 'Invalid reset link'}
                </Banner>
                <Text as="p" variant="bodyMd">
                  This password reset link is invalid or has expired.
                </Text>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  variant="primary"
                  fullWidth
                >
                  Request new reset link
                </Button>
              </BlockStack>
            </Card>
          ) : (
            <>
              {/* Error Banner */}
              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}

              {/* Form Card */}
              <Card>
                <BlockStack gap="500">
                  <Text as="h2" variant="headingMd">
                    Set new password
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Enter your new password below.
                  </Text>

                  <Form onSubmit={handleSubmit}>
                    <BlockStack gap="400">
                      <TextField
                        label="New password"
                        type="password"
                        requiredIndicator value={password}
                        onChange={setPassword}
                        autoComplete="new-password"
                        disabled={isSubmitting}
                        autoFocus
                        helpText={
                          password.length > 0 ? (
                            <BlockStack gap="100">
                              {passwordStrength.requirements.map((req, i) => (
                                <Text
                                  key={i}
                                  as="span"
                                  variant="bodySm"
                                  tone={req.met ? 'success' : 'subdued'}
                                >
                                  {req.met ? '✓' : '○'} {req.message}
                                </Text>
                              ))}
                            </BlockStack>
                          ) : undefined
                        }
                      />
                      <TextField
                        label="Confirm new password"
                        type="password"
                        requiredIndicator value={confirmPassword}
                        onChange={setConfirmPassword}
                        autoComplete="new-password"
                        disabled={isSubmitting}
                        error={
                          confirmPassword && !passwordsMatch
                            ? 'Passwords do not match'
                            : undefined
                        }
                      />
                      <Button
                        submit
                        variant="primary"
                        size="large"
                        fullWidth
                        loading={isSubmitting}
                        disabled={
                          passwordStrength.score < PASSWORD_REQUIREMENTS.length ||
                          !passwordsMatch
                        }
                      >
                        Reset password
                      </Button>
                    </BlockStack>
                  </Form>

                  <Link to="/forgot-password" style={{ color: '#2C6ECB' }}>
                    Request new reset link
                  </Link>

                  <Link to="/forgot-password" style={{ color: '#2C6ECB' }}>
                    Request new reset link
                  </Link>

                  <Box paddingBlockStart="200">
                    <Text as="p" variant="bodyMd" alignment="center">
                      <Link
                        to="/login"
                        style={{
                          color: '#2C6ECB',
                          textDecoration: 'none',
                          fontWeight: 500,
                        }}
                      >
                        Back to sign in
                      </Link>
                    </Text>
                  </Box>
                </BlockStack>
              </Card>
            </>
          )}

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenlyapp.com" style={{ color: '#2C6ECB' }}>
              support@whenlyapp.com
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}
